import Api from '@/api'
import Store from '@/store'

import SessionStorage from '../helpers/sessionStorage'
import Storage from '@/helpers/storage'

import Firebase from '@/modules/firebase'
import I18n from '@/modules/i18n'
import Loop from '@/modules/loop'
import Tag from '@/modules/tag'
import Tracking from '@/modules/tracking'
import UserAgent from '@/modules/userAgent'
import UserFlow from '@/modules/userFlow'
import ENUM from '@/assets/ENUM'

const auth = {

    Statuses: {
        SUCCESS: 1,
        UNKNOWN_ERROR: 0,
        INVALID_CREDENTIALS: 2,
        TIMEOUT: 3,
        APPLICATION_PASSWORD: 4,
        IMAP_DISABLED: 5
    },

    errorToStatus: function (error) {
        switch (error) {
            case ENUM.ERRORS.INVALID_CREDENTIALS:
                return this.Statuses.INVALID_CREDENTIALS
            case ENUM.ERRORS.TIMEOUT:
                return this.Statuses.TIMEOUT
            case ENUM.ERRORS.APPLICATION_PASSWORD:
                return this.Statuses.APPLICATION_PASSWORD
            case ENUM.ERRORS.IMAP_DISABLED:
                return this.Statuses.IMAP_DISABLED
            default:
                return this.Statuses.UNKNOWN_ERROR
        }
    },

    redirect: function (to, from, next) {

        Storage.getItem('token') ? next({path: '/newsletters'}) : auth.disconnect()

    },

    connect: async function (Vue, email, password, provider, {oAuthCode, oAuthCodeVerifier}, server, port, secured) {

        Tag.infoAll(Vue, 'status_connection_started', provider)

        const device = UserAgent.getDevice()
        const language = I18n.get(Vue)
        const millisecondsToWait = await Firebase.getValue('login_timeout_seconds', 'Number') * 1000 || 60 * 1000
        // login_timeout_seconds is the maximum time allowed to connect successfully.
        // In some cases, the login_timeout_seconds is used to manage the total time (several failed attemps can occured
        // delayed using the value login_delay_retry_seconds)
        // In other cases, failed attempts are not retried (oauth case), but the login_timeout_seconds is still used
        // to manage the end of the loop on /auth/check.
        const deadLineTime = new Date().getTime() + millisecondsToWait
        const millisecondsToDelay = await Firebase.getValue('login_delay_retry_seconds', 'Number') * 1000 || 30 * 1000


        const params = {email, language, device, provider, password, server, port, secured}
        if (oAuthCodeVerifier) {
            params.oAuthCode = oAuthCode
            params.oAuthCodeVerifier = oAuthCodeVerifier
        }
        return await connectRec(Vue, params, {deadLineTime, millisecondsToDelay})
    },

    disconnect: function (requestToServer) {
        // to prevent disconnect loops
        if (Storage.getItem('token') || requestToServer) {
            Api.auth.disconnect()
        }
        if (Store) { // fixme: Iphone crash
            Store.dispatch('disconnect')
        }
        Storage.clear()
        SessionStorage.clear()
        const lang = I18n.getLangFromUrl()
        location.href = `/${lang}`

    },

    sendMagicLinkEmail: function (email, lang) {

        return Api.auth.sendMagicLinkEmail(email, lang)

    },

    getMagicLinkUser: function (code) {

        return Api.auth.getMagicLinkUser(code)

    },

    deleteEmailAccount: function (email) {

        return Api.auth.deleteEmailAccount(email)
            .then(data => {
                Storage.setItem('aToken', data.token)
                return Loop.start(() => Api.auth.deleteEmailAccountCheck(email))
            })

    },

    validConsent: function (lang, email) {

        const utms = Tracking.getTracking()

        const body = {
            email,
            language: lang,
            device: UserAgent.getDevice()
        }

        return Api.auth.validConsent(body, utms)

    },

    reconnect(vue, mailbox) {
        SessionStorage.setItem(SessionStorage.KEYS.EMAIL, mailbox.email)
        return Api.provider.get(mailbox.email)
            .then(data => {
                const path = UserFlow.getLoginPathUsingProviderAndInvalidGrant(data)
                vue.$router.push({path})
            }).catch(error => {
                console.error('Provider API call failed during reconnect: ', error)
                vue.$router.push({path: `/login/${mailbox.provider}`})
            })
    }

}

function shouldRetryToConnect(deadLineTime, errorMsg, oAuthCodeVerifier) {
    return ((new Date().getTime() < deadLineTime) &&
        (errorMsg === 'timeout' || errorMsg === 'unknown_error') &&
        // if oAuthCodeVerifier is defined, we don't retry (oauth case)
        // If we want to retry for oauth case, we probably need to regenerate the oAuthCode/oAuthCodeVerifier
        !oAuthCodeVerifier
    )
}

async function connectRec(Vue, {email, password, language, device, provider, oAuthCode, oAuthCodeVerifier, server, port, secured}, {deadLineTime, millisecondsToDelay}) {
    try {
        const data =  await Api.auth.connect(email, password, device, language, {oAuthCode, oAuthCodeVerifier}, server, port, secured)
        return await postConnection(Vue, data.token, deadLineTime)
    } catch(error) {
        if (error) {
            const errorStatus = auth.errorToStatus(error.message)

            Tag.infoGa(Vue, 'status_connection_failed', provider, errorStatus)

            if (shouldRetryToConnect(deadLineTime, error.message, oAuthCodeVerifier)) {
                await new Promise((resolve) => setTimeout(() => resolve(null), millisecondsToDelay))
                return await connectRec(Vue, {email, password, language, device, provider, oAuthCode, oAuthCodeVerifier, server, port, secured},
                    {deadLineTime, millisecondsToDelay})
            } else {
                throw error
            }
        } else {
            throw new Error('unknown_error')
        }
    }
}

export default auth

function postConnection(Vue, aToken, deadLineTime) {

    Storage.setItem('aToken', aToken)

    return Loop.start(Api.auth.authCheck, undefined, undefined, deadLineTime)
        // eslint-disable-next-line complexity
        .then(data => {
            const result = data.result

            Storage.setItem('token', result.token)
            Store.dispatch('setUser', result.user)

            const provider = result.user.accounts[0].provider
            switch (result.status) {
                case ENUM.LOGIN_STATUS.CREATED:
                    Storage.setItem('userHash', result.user.hash) //gtm pixels
                    Tag.accountCreated(provider)
                    Tag.accountCreatedAmplitude(Vue, provider)
                    break
                case ENUM.LOGIN_STATUS.ASSOCIATED:
                    Tag.accountAssociated()
                    Tag.accountAssociatedAmplitude(Vue)
                    break
                case ENUM.LOGIN_STATUS.IMPORTED:
                    Tag.accountImportedAmplitude()
                    break
                case ENUM.LOGIN_STATUS.RECONNECTED:
                    Tag.accountReconnectedAmplitude(Vue)
                    break
                case ENUM.LOGIN_STATUS.RECONNECTED_INVALID_GRANT:
                    Tag.accountReconnectedWasInvalidGrantAmplitude(Vue)
                    break
                    // Magic link use not postConnection()
                    // case ENUM.LOGIN_STATUS.MAGIC_LINK:
                    //     Tag.accountReconnectedMagicLinkAmplitude()
                    // break
                default:
                    //TODO: delete "default" after server update with "status" in /auth/check response
                    if (result.new) {
                        Storage.setItem('userHash', result.user.hash) //gtm pixels
                        Tag.accountCreated(provider)
                        Tag.accountCreatedAmplitude(Vue, provider)
                    }

                    if (result.association) {
                        Tag.accountAssociated()
                        Tag.accountAssociatedAmplitude(Vue)
                    }
            }

            return result
        })
}
